import {GalleryImageItem} from '@components/commons/gallery-image-item/GalleryImageItem.tsx'
import {
    OpsVettingDisabledContainer,
    OpsVettingDragButton,
    OpsVettingGalleryItemsContainer,
    OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms/OpsVettingAtoms.tsx'
import {Expand01Icon, MoveIcon, Trash01Icon} from '@components/ui/icon/Icon.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {memo, ReactNode} from 'react'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {Flexbox} from '@/components/ui/flexbox/FlexBox.tsx'
import {StyledDot, StyledText} from './style.ts'
import {Chip} from '@/components/commons/chip/Chip.tsx'

interface ProPhotosGalleryItemProps {
    imageUrl: string
    imageName: string
    onClickExpand: () => void
    bottomLeftSlot: ReactNode
    isPendingImage: boolean
    isHostApprovalNeeded: boolean
    isProPhotography: boolean
    imageId: number
    onDeleteModalOpen: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export const ProPhotosGalleryItem = memo(
    ({
        imageUrl,
        bottomLeftSlot,
        isPendingImage,
        isHostApprovalNeeded,
        isProPhotography,
        imageId,
        imageName,
        onClickExpand,
        onDeleteModalOpen
    }: ProPhotosGalleryItemProps) => {
        const {t} = useTranslation()
        const {attributes, setNodeRef, transform, transition, isDragging, listeners} = useSortable({id: imageId})

        const style = {
            transform: CSS.Translate.toString(transform),
            transition
        }

        return (
            <>
                <OpsVettingDisabledContainer $isDisabled={isDragging} ref={setNodeRef} style={style} {...attributes}>
                    <OpsVettingGalleryItemsContainer direction={'column'} gap={4} fullWidth>
                        <GalleryImageItem
                            src={imageUrl}
                            bottomLeftSlot={bottomLeftSlot}
                            topRightSlot={
                                <Button
                                    variant={'secondary'}
                                    iconOnly
                                    onClick={onDeleteModalOpen}
                                    className="show-on-hover"
                                >
                                    <Trash01Icon />
                                </Button>
                            }
                            bottomRightSlot={
                                <Button
                                    variant="secondary"
                                    iconOnly
                                    className="show-on-hover"
                                    size="sm"
                                    onClick={onClickExpand}
                                >
                                    <Expand01Icon />
                                </Button>
                            }
                            topLeftSlot={
                                <OpsVettingDragButton
                                    {...listeners}
                                    variant="secondary"
                                    $isDragging={isDragging}
                                    iconOnly
                                    className="show-on-hover"
                                    size="sm"
                                >
                                    <MoveIcon size={16} />
                                </OpsVettingDragButton>
                            }
                        />

                        <Flexbox direction={'column'} gap={1} fullWidth>
                            <OpsVettingImageTitle>{imageName}</OpsVettingImageTitle>
                            <Flexbox align={'center'} justify={'space-between'} gap={1.5} fullWidth>
                                {isProPhotography ? (
                                    <StyledText>{t('vetting:reordering:pro_photo')}</StyledText>
                                ) : (
                                    isHostApprovalNeeded == false && (
                                        <StyledText>{t('vetting:reordering:host_provided')}</StyledText>
                                    )
                                )}

                                {isPendingImage && (
                                    <Chip
                                        startSlot={<StyledDot />}
                                        label={t('vetting:reordering:pending')}
                                        variant={'warning'}
                                        isActive
                                        shape="roundedRectangle"
                                    />
                                )}
                            </Flexbox>
                        </Flexbox>
                    </OpsVettingGalleryItemsContainer>
                </OpsVettingDisabledContainer>
            </>
        )
    }
)
